import { useEffect, useState } from "react";

//MUI components
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { styled } from "@mui/material/styles";

//AJCE components
import { useMaterialUIController } from "context";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { filterBySemBranch } from "framework/students/advanced_search";
import { useSearchParams } from "react-router-dom";
import { branches } from "utils/data";
import { semesters } from "utils/data";

const CustomFormControl = styled(FormControl)(({ theme }) => {
  const { palette } = theme;
  return {
    "& .MuiOutlinedInput-root": {
      height: "37.25px",
      "& fieldset": {
        border: "1px solid #bababa",
      },
    },
    "& .MuiOutlinedInput-root.Mui-error": {
      "& fieldset": {
        border: `2px solid ${palette.error.main}`,
      },
      "&:hover fieldset": {
        border: `2px solid ${palette.error.main}`,
      },
    },
  };
});

function FilterSection({
  countPerFetch,
  loadingState,
  resultState,
  searchedFilterState,
  setLastDocSnapshot,
  setSnackbar,
}) {
  const [filter, setFilter] = useState({
    sem: "",
    branch: "",
  });
  const [isLoading, setIsLoading] = loadingState;
  const [searchedFilter, setSearchedFilter] = searchedFilterState;
  const [result, setResult] = resultState;

  const [searchParams, setSearchParams] = useSearchParams();

  const [MUIController] = useMaterialUIController();

  useEffect(() => {
    if (result.length !== 0) return;
    const sem = (searchParams.get("sem") ?? "").trim();
    const branch = (searchParams.get("branch") ?? "").trim();
    let count = Number.parseInt(searchParams.get("count") ?? countPerFetch);
    count = Math.max(count, countPerFetch);
    count = count % countPerFetch === 0 ? count : null;
    if (
      (sem === "" && branch === "") ||
      !branches.includes(branch) ||
      !semesters.includes(sem) ||
      count === 0
    ) {
      setSearchParams({});
      return;
    }
    setFilter({ sem, branch });

    (async () => {
      try {
        setIsLoading(true);
        setResult([]);
        const { result: newResult, lastDocSnapshot } = await filterBySemBranch(
          { sem, branch },
          count,
          null
        );
        setResult(newResult);
        setLastDocSnapshot(lastDocSnapshot);
        setSearchedFilter({ sem, branch });
      } catch (err) {
        console.error(err);
        setFilter({
          sem: "",
          branch: "",
        });
        const msg = "Something went wrong while searching try again.";
        setSnackbar({
          description: err?.message ?? msg,
          type: "error",
          icon: "error",
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [setFilter, searchParams]);

  async function handleSearch() {
    if (filter.sem === "" && filter.branch === "") {
      setSnackbar({
        description: "Select atleast one of the filters before searching.",
        type: "error",
        icon: "error",
      });
      return;
    }
    let isSameFilter = true;
    Object.keys(filter).forEach((key) => {
      if (filter[key] !== searchedFilter?.[key]) {
        isSameFilter = false;
        return;
      }
    });
    if (isSameFilter) {
      setSnackbar({
        description: (
          <>
            This search result is already being shown.
            <br /> Change the filter to search again or <br />
            load more results from the bottom of the list.
          </>
        ),
        type: "warning",
        icon: "star",
      });
      return;
    }
    try {
      setIsLoading(true);
      setResult([]);
      setLastDocSnapshot(null);
      const { result: newResult, lastDocSnapshot } = await filterBySemBranch(
        filter,
        countPerFetch,
        null
      );
      setResult(newResult);
      setLastDocSnapshot(lastDocSnapshot);
      setSearchedFilter({ ...filter });
      setSearchParams({ ...filter, count: newResult.length });
    } catch (err) {
      console.error(err);
      const msg = "Something went wrong while searching try again.";
      setSnackbar({
        description: err?.message ?? msg,
        type: "error",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", md: "row", lg: "row" }}
        justifyContent="center"
        alignItems="center"
        p={2}
      >
        <MDBox display="flex" alignItems="center" minWidth="12rem" width={"100%"} m={1}>
          <CustomFormControl fullWidth size="small">
            <InputLabel id="branch-select-label">Branch</InputLabel>
            <Select
              labelId="branch-select-label"
              id="branch-select"
              label="Branch"
              value={filter.branch}
              onChange={(e) => {
                setFilter((prev) => {
                  return { ...prev, branch: e.target.value };
                });
              }}
            >
              {branches.map((branch) => (
                <MenuItem value={branch} key={`id-${branch}`}>
                  {branch !== "" ? branch : "--- No Selection ---"}
                </MenuItem>
              ))}
            </Select>
          </CustomFormControl>
        </MDBox>
        <MDBox display="flex" alignItems="center" minWidth="12rem" width={"100%"} m={1}>
          <CustomFormControl size="small" fullWidth>
            <InputLabel id="sem-select-label">Semester</InputLabel>
            <Select
              labelId="sem-select-label"
              id="sem-select"
              label="Semester"
              value={filter.sem}
              onChange={(e) => {
                setFilter((prev) => {
                  return { ...prev, sem: e.target.value };
                });
              }}
            >
              {semesters.map((semester) => (
                <MenuItem value={semester} key={`id-${semester}`}>
                  {semester !== "" ? semester : "--- No Selection ---"}
                </MenuItem>
              ))}
            </Select>
          </CustomFormControl>
        </MDBox>
        <MDBox display="flex" alignItems="center" width="100%" m={1}>
          <MDButton
            fullWidth
            variant="gradient"
            color={MUIController.sidenavColor}
            disabled={isLoading}
            onClick={handleSearch}
          >
            Search
          </MDButton>
        </MDBox>
      </MDBox>
    </>
  );
}

export default FilterSection;
